import "@infinite-table/infinite-react/index.css";
import {
	DataSource,
	type DataSourceApi,
	type DataSourceDataParams,
	type DataSourceLivePaginationCursorFn,
	type DataSourceSingleSortInfo,
	type DataSourceSortInfo,
	InfiniteTable,
	type InfiniteTablePropColumns,
} from "@infinite-table/infinite-react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import type { MetadataTypes } from "epsm-api-types";
import {
	type ColumnType,
	type Monitor as MonitorState,
	SCREENER_COLUMNS,
	type ScreenerColumn,
	type ScreenerData,
	type ScreenerSubField,
} from "epsm-api-types/models";
import {
	type Dispatch,
	type SetStateAction,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import { Button } from "@/components/ui/button";
import { Columns } from "@/components/ui/columns";
import { ColumnSet } from "@/components/ui/columnset";
import { Groups } from "@/components/ui/groups";
import { Toaster } from "@/components/ui/toaster";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { INFINITY_TABLE } from "@/config";
import { useWidgetView } from "@/context/WidgetViewContext";
import { useCellContextMenuItems } from "@/hooks/useCellContextMenuItems";
import { useColumnMenuItems } from "@/hooks/useColumnMenuItems";
import { useColumnSizing } from "@/hooks/useColumnSizing";
import {
	columnPinning,
	MAX_COLUMN_WIDTH,
	MIN_COLUMN_WIDTH,
} from "@/hooks/useColumnSizing/configs";
import { useEventSubscription } from "@/hooks/useEventSubscription";
import { getAlign, getParams } from "@/lib/utils";
import { settings } from "@/lib/utils/settings/settings";
import ErrorHandler from "@/services/ErrorService/ErrorService";
import {
	DEFAULT_GRID_PAGE_SIZE,
	DEFAULT_STATE_VALUES,
} from "@/shared/constants/common";
import { EVENTS } from "@/shared/eventEmiters/consts";
import type { EventEmitter } from "@/shared/eventEmiters/EventEmitter";
import { getSort } from "@/shared/helpers/getSort";
import { getSortInfo } from "@/shared/helpers/getSortInfo";
import sprites from "/src/shared/assets/sprite.svg";
import { UseData as UsePriceData } from "../../Chart/services/UseData";
import { formatValue, parseNumber } from "../../helpers/FormattingHelpers";
import { arraysEqualUnordered } from "../../Screener/lib/helpers/arraysEqualUnordered/arraysEqualUnordered";
import { ScreenerColumnService } from "../../Screener/services/ScreenerColumnService";
import { UseData } from "../../Screener/services/UseData";
import { DEFAULT_STATE } from "../defaultState";
import { columnValueFormatter } from "../lib/helpers/columnValueFormatter";
import { exportCsvData } from "../lib/helpers/exportCsvData";
import { filterWidgetState } from "../lib/helpers/filterWidgetState";
import { getColumnTitle } from "../lib/helpers/getColumnTitle";
import { mergeFields } from "../lib/helpers/mergeFields";
import { removeItemInCache } from "../lib/helpers/removeItemInCache";
import { renderSparklines } from "../lib/helpers/renderSparklines";
import { useCellClickHandler } from "../lib/hooks/useCellClickHandler";
import type { MonitorState as TabsMonitorState } from "../type";

const DEFAULT_PAGE_SIZE = DEFAULT_GRID_PAGE_SIZE;

const getType = (type: ColumnType): "string" | "number" | "date" | string => {
	switch (type) {
		case "date":
			return "date";
		case "num":
			return "number";
	}
	return "string";
};

let totalCount = 0;

export const Monitor = ({
	state: widgetState,
	eventEmitter,
	width,
	tileId,
	onStateChange,
}: {
	state: TabsMonitorState;
	eventEmitter: EventEmitter;
	width?: number;
	tileId: string;
	onStateChange?: OnStateChange;
}) => {
	const ticker = widgetState?.ticker ?? DEFAULT_STATE_VALUES.ticker;
	const { visibleColumns, setVisibleColumns } = useWidgetView();
	const { execute, isLoading } = UseData.create().useGetData();
	const { execute: executePriceData, isLoading: isLoadingPriceData } =
		UsePriceData.create().useGetData();

	const [isWindowExpanded, setIsWindowExpanded] = useState<boolean>(false);
	const [columnOrder, setColumnOrder] = useState<string[]>([]);
	const [state, setState] = useState<MonitorState>({
		...(!Object.keys(filterWidgetState(DEFAULT_STATE, widgetState)).length
			? DEFAULT_STATE
			: filterWidgetState(DEFAULT_STATE, widgetState)),
	});
	const fetchDataRef = useRef<string[] | DataSourceSortInfo<ScreenerData>>([]);
	const tickersRef = useRef<string[]>(state.tickers || []);
	const [priceData, setPriceData] = useState<
		Record<string, { field: number[] }>
	>({});
	const [dataSourceApi, setDataSourceApi] =
		useState<DataSourceApi<ScreenerData> | null>(null);
	const [useCustomOrder, setUseCustomOrder] = useState(false);

	useEffect(() => {
		eventEmitter.emit(EVENTS.ID, tileId);
	}, [eventEmitter, tileId]);

	useEffect(() => {
		ScreenerColumnService.create()
			.getData()
			.then((columns) => {
				if (!state?.fields?.length) return;

				const columnMap: Record<string, ScreenerColumn> = {};

				for (const fieldPath of state.fields) {
					const mainField = fieldPath.split(".")[0];

					const column = columns.data?.find((col) => col.name === mainField);
					if (!column) continue;

					const filterSubFields = (
						subFields: ScreenerSubField[],
						currentPath: string[],
					): ScreenerSubField[] => {
						return subFields
							.filter((subField) => {
								const fullPath = [...currentPath, subField.name].join(".");

								return state.fields?.some((stateField) => {
									return stateField.startsWith(fullPath);
								});
							})
							.map((subField) => ({
								...subField,
								...(subField.subFields?.length && {
									subFields: filterSubFields(subField.subFields, [
										...currentPath,
										subField.name,
									]),
								}),
							}));
					};

					const updatedColumn = {
						...column,
						...(column.subFields?.length && {
							subFields: filterSubFields(column.subFields, [mainField]),
						}),
					};

					columnMap[mainField] = updatedColumn;
				}

				const filteredColumns = Object.values(columnMap);

				setVisibleColumns((prevVisibleColumns) => ({
					...prevVisibleColumns,
					Monitor: filteredColumns,
				}));
			});
	}, [state, setVisibleColumns]);

	const queryClient = useQueryClient();

	const {
		panelName,
		viewportReservedWidth,
		getColumnSizing,
		columnSizing,
		onColumnSizingChange,
		resetColumnSize,
	} = useColumnSizing(tileId);

	const fetchAndCacheTickerPrices = async () => {
		if (!data?.pages?.length) return priceData;

		const tickers = data.pages.map((page) => page?.ticker);
		const newTickers = tickers.filter((ticker) => !priceData[ticker]);

		if (!newTickers.length) return priceData;

		const fetchTickerPrices = async (ticker: string) => {
			const result = await executePriceData({
				params: {
					filter: [{ name: "ticker", condition: "eq", value: ticker }],
					fields: ["ticker", "adj_close"],
					sort: [{ name: "date", order: -1 }],
					pagination: { limit: 5, offset: 0 },
				},
			});

			return {
				ticker,
				field:
					result?.data
						?.map((price) => price.close)
						.toReversed()
						.filter((v) => v !== undefined) || [],
			};
		};

		const results = await Promise.allSettled(newTickers.map(fetchTickerPrices));

		const newPrices: Record<string, { field: number[] }> = { ...priceData };

		for (const result of results) {
			if (result.status === "fulfilled") {
				const { ticker, field } = result.value;
				newPrices[ticker] = { field };
			} else {
				ErrorHandler.logError(
					`Failed to fetch prices for ticker: ${result.reason}`,
				);
			}
		}

		setPriceData(newPrices);

		return newPrices;
	};

	const renderProcess = async (name: string) => {
		switch (name) {
			case "renderSparklines": {
				const prices = await fetchAndCacheTickerPrices();

				return {
					render: ({ data }: { data?: { ticker?: string } | null }) => {
						if (!data?.ticker || !prices) return;

						return renderSparklines(data.ticker, prices);
					},
				};
			}
		}
		return null;
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <we don't want to run effect on every state change>
	useEffect(() => {
		if (!ticker) return;

		const newTickers = tickersRef.current.includes(ticker)
			? tickersRef.current
			: [...tickersRef.current, ticker];

		if (newTickers.length > tickersRef.current.length) {
			fetchDataRef.current = newTickers;
		}
		setTickers(newTickers);
		setState({
			...state,
			tickers: tickersRef.current,
			fields: columnOrder,
		});
	}, [ticker]);

	const getData = async (dataInfo: DataSourceDataParams<ScreenerData>) => {
		const types: MetadataTypes = {};

		for (const columnName of columnOrder) {
			const [mainField] = columnName.split(".");
			const column = SCREENER_COLUMNS.find((item) => item.name === mainField);

			if (column) {
				types[columnName] = column.type;
			}
		}

		const result =
			tickersRef.current.length !== 0
				? await execute({
						params: {
							...getParams(
								dataInfo,
								columnOrder.length ? columnOrder : undefined,
								DEFAULT_PAGE_SIZE,
							),
							filter: [
								{
									value: tickersRef.current,
									condition: "in",
									name: "ticker",
								},
							],
							types,
						},
					})
				: null;

		if (result?.metadata?.total) {
			totalCount = result.metadata.total;
		}
		return {
			data: result?.data || [],
			hasMore: result?.metadata?.pagination?.nextPage,
			totalCount,
		};
	};

	const [_scrollTopId, setScrollTop] = useState(0);

	useEffect(() => {
		// when sorting changes, scroll to the top
		setScrollTop(Date.now());
	}, []);

	const [dataParams, setDataParams] = useState<
		Partial<DataSourceDataParams<ScreenerData>>
	>({
		sortInfo: getSortInfo(state.sort),
		livePaginationCursor: 0,
	});

	const { data, fetchNextPage, isFetchingNextPage, refetch } = useInfiniteQuery(
		{
			queryKey: [
				"ScreenerData",
				fetchDataRef.current,
				columnOrder,
				tickersRef.current,
				dataParams.sortInfo,
			],
			queryFn: async ({ pageParam = 0 }) => {
				const params: DataSourceDataParams<ScreenerData> = {
					originalDataArray: [],
					sortInfo: dataParams.sortInfo,
					livePaginationCursor: pageParam,
				};
				return await getData(params);
			},
			initialPageParam: 0,
			getNextPageParam: (lastPage, allPages) => {
				if (lastPage?.hasMore) {
					return (allPages?.length ?? 0) * DEFAULT_PAGE_SIZE;
				}
				return undefined;
			},
			select: (data) => {
				const flatData = data.pages.flatMap((x) => x.data);

				const result = {
					pages: flatData,
				};

				return result;
			},
		},
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: columnOrder call reload data
	const getColumns = useCallback(
		async (
			setColumnOrder: Dispatch<SetStateAction<string[]>>,
		): Promise<InfiniteTablePropColumns<ScreenerData>> => {
			const newColumnOrder: string[] = [];
			const result: InfiniteTablePropColumns<ScreenerData> = {};

			const processNestedSubfields = (
				column: ScreenerColumn,
				subFields: ScreenerSubField[],
				parentName: string,
				parentTitle: string,
			) => {
				for (const subfield of subFields) {
					const fullColumnName = `${parentName}.${subfield.name}`;
					const fullTitle =
						parentName === column.name
							? getColumnTitle(subfield, true)
							: `${parentTitle} > ${getColumnTitle(subfield, true)}`;
					const displayTitle = `${getColumnTitle(column)} (${fullTitle})`;

					if (subfield.subFields?.length) {
						processNestedSubfields(
							column,
							subfield.subFields,
							fullColumnName,
							fullTitle,
						);
					} else {
						if (!newColumnOrder.includes(fullColumnName)) {
							newColumnOrder.push(fullColumnName);
						}

						result[fullColumnName] = {
							field: fullColumnName,
							...getColumnSizing(fullColumnName),
							defaultSortable: !column.render,
							headerAlign: "start",
							align: getAlign(column.type),
							header: () => (
								<Tooltip>
									<TooltipTrigger asChild>
										<p className="text-ellipsis overflow-hidden whitespace-nowrap">
											{displayTitle}
										</p>
									</TooltipTrigger>
									<TooltipContent className="custom-fade-in">
										{displayTitle}
									</TooltipContent>
								</Tooltip>
							),
							type: getType(column.type),
							valueFormatter: ({ data, field }) => {
								if (
									data &&
									typeof data[column.name] === "object" &&
									data[column.name] !== null
								) {
									const columnWithSubFieldsPathArray = field
										?.toString()
										?.split(".");

									const getValue = (
										obj: Record<string, any>,
										pathArray: string[],
									) => {
										if (!pathArray.length) return;

										return pathArray.reduce((acc: any, key: string) => {
											if (acc && typeof acc === "object" && key in acc) {
												return acc[key];
											}
											return;
										}, obj);
									};

									const value = getValue(
										data,
										columnWithSubFieldsPathArray || [],
									);

									return columnValueFormatter(value, column);
								}
							},
							...(column.fncClassName && {
								className: ({ value }) =>
									column.fncClassName!(
										column.type === "num"
											? (parseNumber(value) as number)
											: value,
									),
							}),
						};
					}
				}
			};

			for (const column of visibleColumns.Monitor) {
				const columnName = column.name;

				if (column.subFields?.length) {
					processNestedSubfields(
						column,
						column.subFields,
						columnName,
						getColumnTitle(column),
					);
				} else {
					newColumnOrder.push(columnName);

					result[columnName] = {
						field: columnName,
						...getColumnSizing(columnName),
						headerAlign: "start",
						align: getAlign(column.type),
						header: () => (
							<Tooltip>
								<TooltipTrigger asChild>
									<p className="text-ellipsis overflow-hidden whitespace-nowrap">
										{column.titleAbbreviation ||
											column.titleShort ||
											column.title}
									</p>
								</TooltipTrigger>
								<TooltipContent side="right" className="custom-fade-in">
									<p>{column.title}</p>
								</TooltipContent>
							</Tooltip>
						),
						type: getType(column.type),
						valueFormatter: ({ value }) =>
							column.options?.[value as keyof typeof column.options] ??
							formatValue(value, column.type, column.options),
						...(column.fncClassName && {
							className: ({ value }) =>
								column.fncClassName!(
									column.type === "num"
										? (parseNumber(value) as number)
										: value,
								),
						}),
						...(!widgetState?.isDeleteButtonHidden &&
							columnName === "ticker" && {
								render: ({ data }) => (
									<div className="flex gap-2">
										<Button
											className="h-4 p-0 bg-transparent hover:bg-transparent"
											onClick={() => {
												setTickers(
													tickersRef.current.filter(
														(ticker) => ticker !== data?.ticker,
													),
												);
												setState({
													...state,
													tickers: tickersRef.current,
												});
												removeItemInCache({
													ticker: data?.ticker,
													queryClient,
													fetchDataRef,
													columnOrder,
												});
											}}
										>
											<svg
												aria-label="minus-btn"
												role="img"
												stroke="#C3C3C3"
												className="w-4 h-4"
											>
												<use xlinkHref={`${sprites}#minus`} />
											</svg>
										</Button>
										{data?.ticker}
									</div>
								),
							}),
						...(column.render && (await renderProcess(column.render))),
					};
				}
			}

			// replace column order
			const screenerFields = state.fields || [];
			const justReplacedColumn = arraysEqualUnordered(
				screenerFields,
				newColumnOrder,
			);
			setColumnOrder(justReplacedColumn ? screenerFields : newColumnOrder);

			return result;
		},
		[visibleColumns.Monitor, panelName, state?.fields, setState, data],
	);

	const [columns, setColumns] = useState<
		InfiniteTablePropColumns<ScreenerData>
	>({});

	useEffect(() => {
		const columnsCall = async () => {
			const result = await getColumns(setColumnOrder);
			setColumns(result);
		};
		columnsCall();
	}, [getColumns]);

	const onDataParamsChange = useCallback(
		(dataParams: DataSourceDataParams<ScreenerData>) => {
			const params = {
				sortInfo: dataParams.sortInfo,
				livePaginationCursor: dataParams.livePaginationCursor,
			};

			setDataParams(params);
			fetchDataRef.current = dataParams.sortInfo ?? [];
		},
		[],
	);

	const livePaginationCursorFn: DataSourceLivePaginationCursorFn<ScreenerData> =
		useCallback(({ length }) => {
			return length;
		}, []);

	const onCellClick = useCellClickHandler(tileId, (ticker: string) => {
		onStateChange?.({ ticker });
	});

	const getCellContextMenuItems = useCellContextMenuItems();

	const getColumnMenuItems = useColumnMenuItems(
		"Monitor",
		visibleColumns.Monitor,
		setVisibleColumns,
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: correct deps array
	useEffect(() => {
		if (!state.tickers?.length) {
			return;
		}
		const eventSource = new EventSource(
			`${settings.wsHost}/live/monitor/${state.tickers.join(",")}`,
		);
		eventSource.onerror = (error) => {
			ErrorHandler.logError(error);
		};
		const processData = (item: Partial<ScreenerData>) => {
			const { ticker, ...tickerParams } = item;
			const normalizedSearchTicker = ticker?.toUpperCase();
			const tickerToUpdate = data?.pages.find(
				(d) => d?.ticker?.toUpperCase() === normalizedSearchTicker,
			);
			if (!tickerToUpdate || !Object.keys(tickerParams).length) return;

			const dataToUpdate = mergeFields(tickerToUpdate, tickerParams);

			dataSourceApi?.updateData(dataToUpdate);
		};
		eventSource.onmessage = (event) => {
			const items = JSON.parse(event.data) as Partial<ScreenerData>[];
			for (const item of items) {
				processData(item);
			}
		};
		eventSource.addEventListener("reload", () => {
			refetch();
		});
		return () => {
			eventSource?.close();
		};
	}, [state.tickers, data]);

	const setTickers = (tickers: string[]) => {
		tickersRef.current = tickers;
	};

	const handleScreenerState = (newState: MonitorState) => {
		setTickers(newState?.tickers || []);
		setDataParams((prevState) => ({
			...prevState,
			sortInfo: getSortInfo(newState.sort),
		}));
		setState(newState);
		refetch();
		resetColumnSize();
	};

	useEventSubscription(
		eventEmitter,
		EVENTS.EXPANDED_WINDOW,
		setIsWindowExpanded,
	);
	useEventSubscription(eventEmitter, EVENTS.WIDGET_STATE, handleScreenerState);

	useEventSubscription(
		eventEmitter,
		EVENTS.DOWNLOAD_CSV,
		exportCsvData(visibleColumns.Monitor, data?.pages ?? [], "Monitor"),
	);

	useEffect(() => {
		const newState = {
			...state,
			tickers: tickersRef.current,
			sort: getSort(dataParams.sortInfo),
			fields: columnOrder.length ? columnOrder : state.fields,
		};
		eventEmitter.emit(EVENTS.WIDGET_PART_STATE, newState);
	}, [state, dataParams.sortInfo, columnOrder, eventEmitter]);

	const rawTableData = data?.pages?.length ? data.pages : [];

	useEffect(() => {
		const enableCustomOrder = () => {
			setUseCustomOrder(true);
		};

		eventEmitter.on(EVENTS.CUSTOM_SORT_ENABLED, enableCustomOrder);

		return () => {
			eventEmitter.off(EVENTS.CUSTOM_SORT_ENABLED, enableCustomOrder);
		};
	}, [eventEmitter]);

	const tableData =
		useCustomOrder && !state.sort?.length && state.tickers?.length
			? state.tickers
					.map((t) => rawTableData.find((d) => d?.ticker === t))
					.filter((d) => d != null)
			: rawTableData;

	const handleOnScrollToBottom = useCallback(
		() => !isFetchingNextPage && fetchNextPage(),
		[isFetchingNextPage, fetchNextPage],
	);

	return (
		<>
			<div
				id="full-h-el"
				className="flex flex-col h-full relative"
				data-testid="monitor-component"
			>
				{isWindowExpanded && (
					<div
						className="flex gap-x-1 py-1 px-0.5 h-60"
						data-testid="expanded-window"
					>
						<Groups baseId="Monitor" />
						<Columns baseId="Monitor" width={width} />
						<ColumnSet baseId="Monitor" />
					</div>
				)}

				<div className="h-full text-xs" data-testid="monitor-table">
					<DataSource<ScreenerData>
						data={tableData}
						primaryKey="ticker"
						sortInfo={dataParams.sortInfo}
						onSortInfoChange={(
							newSortInfo:
								| (DataSourceSingleSortInfo<ScreenerData> | null)
								| DataSourceSingleSortInfo<ScreenerData>[],
						) => {
							setState({
								...state,
								fields: columnOrder,
								sort: getSort(newSortInfo),
							});
						}}
						loading={isLoading || isLoadingPriceData || isFetchingNextPage}
						livePaginationCursor={livePaginationCursorFn}
						onDataParamsChange={onDataParamsChange}
						onReady={setDataSourceApi}
					>
						<InfiniteTable<ScreenerData>
							columns={columns}
							columnOrder={columnOrder}
							onColumnOrderChange={(newColumnOrder) =>
								setState((prev) => ({ ...prev, fields: newColumnOrder }))
							}
							columnMaxWidth={MAX_COLUMN_WIDTH}
							columnMinWidth={MIN_COLUMN_WIDTH}
							columnPinning={
								data?.pages?.[0]?.ticker !== undefined ? columnPinning : {}
							}
							columnSizing={columnSizing}
							viewportReservedWidth={viewportReservedWidth}
							onColumnSizingChange={onColumnSizingChange}
							rowHeight={INFINITY_TABLE.ROW_HEIGHT}
							columnHeaderHeight={INFINITY_TABLE.COLUMN_HEADER_HEIGHT}
							licenseKey={settings.infiniteTableLicenseKey}
							columnDefaultFlex={1}
							onCellClick={onCellClick}
							headerOptions={{
								alwaysReserveSpaceForSortIcon: false,
							}}
							getColumnMenuItems={getColumnMenuItems}
							getCellContextMenuItems={getCellContextMenuItems}
							onScrollToBottom={handleOnScrollToBottom}
						/>
					</DataSource>
				</div>
			</div>
			<Toaster scope={tileId} />
		</>
	);
};
